import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { IconButton } from "@ventumsoftware/react-toolkit";
import { type FC, useEffect, useState } from "react";
import { useApi } from "../api/APIContext";
import { useApiCall, ValidationError } from "../api/api";
import type { Company, Project } from "../api/dto";
import { Autocomplete } from "../components/form/Autocomplete";
import { Input } from "../components/form/Input";
import { ModalFooter } from "../components/layout/ModalProvider";

export const AddInspectionModal: FC<{ company?: Company | null; project?: Project | null }> = (
  props,
) => {
  const { companies, questionSets, projects, refresh } = useApi();
  const { addInspection, generateAssessmentName } = useApiCall();

  const [name, setName] = useState<string>("");
  const [company, setCompany] = useState<string | null>(props.company ? props.company.id : null);
  const [project, setProject] = useState<string | null>(props.project ? props.project.id : null);
  const [questionSet, setQuestionSet] = useState<string | null>(null);

  const [errors, setErrors] = useState<string[]>([]);
  const companyMap = companies.filter((c) => !c.isArchived).map((c) => [c.id, c.name]);
  const selectedCompany = companies.find((c) => c.id === company);

  const projectMap = projects
    .filter((p) => !p.isArchived && p.companyId === company)
    .map((c) => [c.id, c.name]);
  const selectedProject = projects.find((p) => p.id === project);

  const questionSetMap = questionSets
    .filter(
      (q) =>
        ["Fir", "Lir"].includes(q.type) && selectedCompany?.availableQuestionSetIds?.includes(q.id),
    )
    .map((q) => [q.id, q.name]);
  const selectedQuestionSet = questionSets.find(
    (q) => q.id === questionSet && selectedCompany?.availableQuestionSetIds?.includes(q.id),
  );

  // Reset project selection when company does not match selected project
  useEffect(() => {
    if (selectedCompany && selectedProject && selectedCompany.id !== selectedProject.companyId) {
      setProject(null);
    }
  }, [selectedCompany, selectedProject]);

  // Set first project when company only has one option
  useEffect(() => {
    if (selectedCompany && !selectedProject && projectMap.length === 1) {
      setProject(projectMap[0][0]);
    }
  }, [selectedCompany, selectedProject, projectMap]);

  // Reset question set selection when company does not match selected question-set
  useEffect(() => {
    if (
      selectedCompany &&
      questionSet &&
      !selectedCompany.availableQuestionSetIds?.includes(questionSet)
    ) {
      setQuestionSet(null);
    }
  }, [selectedCompany, questionSet]);

  // Set first question-set when company only has one option
  useEffect(() => {
    if (selectedCompany && !selectedQuestionSet && questionSetMap.length === 1) {
      setQuestionSet(questionSetMap[0][0]);
    }
  }, [selectedCompany, selectedQuestionSet, questionSetMap]);

  // Generate inspection name when company and selected question-set updates
  useEffect(() => {
    if (selectedCompany && selectedQuestionSet) {
      generateAssessmentName(selectedCompany, selectedQuestionSet).then((name) => setName(name));
    }
  }, [selectedCompany, selectedQuestionSet, generateAssessmentName]);

  // Reset name if company or question set is unset
  useEffect(() => {
    if (!selectedCompany || !selectedQuestionSet) {
      setName("");
    }
  }, [selectedCompany, selectedQuestionSet]);

  const save = async () => {
    if (!company || !project || !questionSet) {
      return;
    }
    await addInspection(company, project, questionSet, name)
      .then(() => refresh.inspections(true))
      .catch((e) => {
        if (e instanceof ValidationError) {
          setErrors(Object.values(e.errors).flat());
        }
        throw e;
      });
  };
  return (
    <div className={""}>
      <div className={"min-h-71 flex-col items-stretch space-y-3"}>
        <Autocomplete
          label={"Bedrijf"}
          value={company === null ? "-" : company}
          options={Object.fromEntries([["-", "Selecteer bedrijf"], ...companyMap])}
          onChange={(c) => {
            setCompany(c === "-" ? null : c);
          }}
        />
        <Autocomplete
          label={"Project"}
          value={project === null ? "-" : project}
          options={Object.fromEntries([["-", "Selecteer project"], ...projectMap])}
          onChange={(p) => {
            setProject(p === "-" ? null : p);
          }}
        />
        <Autocomplete
          label={"Vragenlijst"}
          value={questionSet === null ? "-" : questionSet}
          options={Object.fromEntries([["-", "Selecteer vragenlijst"], ...questionSetMap])}
          onChange={(q) => {
            setQuestionSet(q === "-" ? null : q);
          }}
        />
        <div className={"flex flex-row items-end space-x-2"}>
          <Input type={"text"} label={"Inspectie naam"} value={name} onChange={setName} />
          <IconButton
            type={"secondary"}
            size={"md"}
            icon={faRefresh}
            disabled={!selectedCompany || !selectedQuestionSet}
            onClick={() =>
              selectedCompany &&
              selectedQuestionSet &&
              generateAssessmentName(selectedCompany, selectedQuestionSet).then((name) =>
                setName(name),
              )
            }
          />
        </div>
      </div>
      <ModalFooter
        text={"Opslaan"}
        disabled={!selectedCompany || !selectedProject || !selectedQuestionSet}
        onSubmit={save}
      />
    </div>
  );
};
